import React from 'react';
import { Shield, Award, Truck, ShieldCheck, Zap, FileText } from 'lucide-react';
import { whyChooseUs } from '../data/mockData';

const iconMap = {
  Shield,
  Award,
  Truck,
  ShieldCheck,
  Zap,
  FileText
};

export const WhyChooseUs = () => {
  return (
    <section className="py-24 bg-gradient-to-br from-gray-900 to-black text-white relative overflow-hidden">
      {/* Background Pattern */}
      <div className="absolute inset-0 opacity-5">
        <div className="absolute inset-0" style={{
          backgroundImage: `url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%23ffffff' fill-opacity='1'%3E%3Cpath d='M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")`
        }}></div>
      </div>

      <div className="container mx-auto px-4 relative z-10">
        <div className="text-center mb-16">
          <div className="inline-block bg-green-500/20 text-green-400 px-4 py-2 rounded-full text-sm font-semibold mb-4 border border-green-500/30">
            Why Choose Us
          </div>
          <h2 className="text-4xl md:text-5xl font-bold mb-6">
            The Next Generation{' '}
            <span className="text-transparent bg-clip-text bg-gradient-to-r from-green-400 to-green-600">
              Difference
            </span>
          </h2>
          <p className="text-xl text-gray-300 max-w-3xl mx-auto">
            Experience the difference that professional expertise, cutting-edge equipment, and genuine care make in every project.
          </p>
        </div>

        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8 max-w-7xl mx-auto">
          {whyChooseUs.map((item, index) => {
            const IconComponent = iconMap[item.icon];
            return (
              <div
                key={item.id}
                className="group bg-white/5 backdrop-blur-sm border border-white/10 rounded-2xl p-8 hover:bg-white/10 hover:border-green-500/50 transition-all duration-300 transform hover:-translate-y-2"
                style={{ animationDelay: `${index * 100}ms` }}
              >
                <div className="bg-gradient-to-br from-green-500 to-green-600 w-16 h-16 rounded-xl flex items-center justify-center mb-6 group-hover:scale-110 group-hover:rotate-6 transition-all duration-300">
                  <IconComponent className="w-8 h-8 text-white" />
                </div>
                <h3 className="text-2xl font-bold mb-3 group-hover:text-green-400 transition-colors">
                  {item.title}
                </h3>
                <p className="text-gray-300 leading-relaxed">
                  {item.description}
                </p>
              </div>
            );
          })}
        </div>

        {/* Featured Achievement - Yard of the Month */}
        <div className="mt-16 max-w-5xl mx-auto">
          <div className="bg-gradient-to-r from-green-500/20 to-green-600/20 border-2 border-green-500/40 rounded-3xl p-8 md:p-12">
            <div className="grid md:grid-cols-2 gap-8 items-center">
              <div className="relative rounded-2xl overflow-hidden shadow-2xl">
                <img
                  src="/yard-of-month.jpg"
                  alt="Yard of the Month - Belle Meade Living Magazine"
                  className="w-full h-auto object-cover"
                />
                <div className="absolute top-4 right-4 bg-green-500 text-white px-4 py-2 rounded-full font-bold text-sm shadow-lg">
                  Award Winner
                </div>
              </div>
              <div className="text-center md:text-left">
                <div className="inline-flex items-center space-x-2 bg-yellow-500/20 text-yellow-400 px-4 py-2 rounded-full text-sm font-semibold mb-4 border border-yellow-500/30">
                  <Award className="w-4 h-4" />
                  <span>Featured Recognition</span>
                </div>
                <h3 className="text-3xl md:text-4xl font-bold mb-4">
                  Yard of the Month
                </h3>
                <p className="text-xl text-green-400 font-semibold mb-4">
                  Belle Meade Living Magazine
                </p>
                <p className="text-gray-300 leading-relaxed mb-6">
                  We're proud that our landscaping work was featured as the <strong className="text-white">Yard of the Month</strong> in the January 2022 issue of Belle Meade Living magazine. This recognition reflects our commitment to creating beautiful, lasting outdoor spaces for our Nashville clients.
                </p>
                <div className="flex flex-wrap gap-3 justify-center md:justify-start">
                  <span className="bg-white/10 px-4 py-2 rounded-full text-sm">Belle Meade</span>
                  <span className="bg-white/10 px-4 py-2 rounded-full text-sm">January 2022</span>
                  <span className="bg-white/10 px-4 py-2 rounded-full text-sm">Landscaping Excellence</span>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Equipment Showcase */}
        <div className="mt-20 grid md:grid-cols-3 gap-6">
          <div className="relative rounded-2xl overflow-hidden h-64 group">
            <img
              src="https://images.unsplash.com/photo-1474742509976-ddec6b387356?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2NzF8MHwxfHNlYXJjaHwzfHxjaGFpbnNhd3xlbnwwfHx8fDE3NzM5NTI3NDl8MA&ixlib=rb-4.1.0&q=85"
              alt="Professional Equipment"
              className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-black/80 to-transparent flex items-end">
              <p className="text-white font-bold text-xl p-6">Professional Grade Equipment</p>
            </div>
          </div>
          <div className="relative rounded-2xl overflow-hidden h-64 group">
            <img
              src="https://customer-assets.emergentagent.com/job_premium-arborcare/artifacts/ydmz1csn_IMG_7599.HEIC"
              alt="Next Generation Service Fleet"
              className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-black/80 to-transparent flex items-end">
              <p className="text-white font-bold text-xl p-6">Modern Service Fleet</p>
            </div>
          </div>
          <div className="relative rounded-2xl overflow-hidden h-64 group">
            <img
              src="https://images.unsplash.com/photo-1591497108596-436c1a1a5c8e?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1ODR8MHwxfHNlYXJjaHwxfHxzdG9ybSUyMGRhbWFnZXxlbnwwfHx8fDE3NzM5NTI3NzR8MA&ixlib=rb-4.1.0&q=85"
              alt="Emergency Response"
              className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-black/80 to-transparent flex items-end">
              <p className="text-white font-bold text-xl p-6">24/7 Emergency Response</p>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};
